import React, { useEffect, useState, useCallback } from "react";
import "./index.css";
import { TagColorOptions, XlsNormalizefileKey } from "../utils/constantsString";
import ChartPlot from "../components/plot";
import Select, { components } from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { storeSelectedValues } from "../Store/TemplateStore/template.action";
import { useTranslation } from "react-i18next";
import { getHeaders } from "../network/cipAPI";
import PopoverText from "../components/PopoverText";
import InfoIconGreen from "../assets/info-icon-green.svg";
import QuestionIcon from "../assets/question-icon.svg";
const Charts = ({ cleaningResponse, date, cips, threshold, activeStep }) => {
  const { t, i18n } = useTranslation();
  const pressure = useSelector((state) => state.pressure);
  
  const normalizedCategories = [
    XlsNormalizefileKey.normalizeTmp,
    XlsNormalizefileKey.normalizeFiltrateFlow,
    // XlsNormalizefileKey.normalizePermeateFlow,
  ];
  const otherCategories = [
    XlsNormalizefileKey.date,
    XlsNormalizefileKey.feedFlowrate,
    XlsNormalizefileKey.filtrateFlowrate,
    // XlsNormalizefileKey.permeateFlow,
    XlsNormalizefileKey.feedPressure,
    XlsNormalizefileKey.filtratePressure,
    // XlsNormalizefileKey.permeatePressure,
    XlsNormalizefileKey.tmpDrop,
    XlsNormalizefileKey.feedSuspended,
    XlsNormalizefileKey.filtrateSuspened,
    XlsNormalizefileKey.feedTemp,
  ];

  const [chartKey, setChartKey] = useState(Date.now().toString());
  const [selectedValues, setSelectedValues] = useState(
    useSelector((state) => state.selectedValues) || []
  );
  const [options, setOptions] = useState(null);
  const [headerOptions, setHeaderOptions] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const dispatch = useDispatch();

  // Reusable function to create value objects with correct header mapping
  const createHeaderMappedValues = useCallback((values, headers) => {
    if (!headers) return values;

    return values.map((item) => ({
      label: item.label,
      value: headers[item.label],
    }));
  }, []);

  const getColor = (key, index) => {
    if (key.label === XlsNormalizefileKey.normalizeFiltrateFlow) {
      return "#00A9E0";
    } else if (key.label === XlsNormalizefileKey.filtrateFlowrate) {
      return "#0000FF";
    } else if (key.label === XlsNormalizefileKey.feedFlowrate) {
      return "#0818A8";
    } else if (key.label === XlsNormalizefileKey.normalizeTmp) {
      return "#58B947";
    } else if (key.label === XlsNormalizefileKey.feedPressure) {
      return "#228B22";
    } else if (key.label === XlsNormalizefileKey.filtratePressure) {
      return "#00A36C";
    } else if (key.label === XlsNormalizefileKey.tmpDrop) {
      return "#40B5AD";
    } else if (key.label === XlsNormalizefileKey.feedSuspended) {
      return "#FF0000";
    } else if (key.label === XlsNormalizefileKey.filtrateSuspened) {
      return "#e4001c";
    } else if (key.label === XlsNormalizefileKey.feedTemp) {
      return "#FF9E19";
    } else if (key.label === XlsNormalizefileKey.date) {
      return "#C900E3";
    }
    return TagColorOptions[index % 10].code;
  };

  const getThreshold = (key) => {
    if (threshold) {
      if (Array.isArray(threshold) && threshold[0]) {
        return [threshold[0][key]];
      } else if (threshold[key]) {
        return [threshold[key]];
      }
    }
    return [];
  };

  const NoOptionsMessage = (props) => {
    return (
      <components.NoOptionsMessage {...props}>
        <span className="custom-css-class">{t("ValueStrings.noOption")}</span>
      </components.NoOptionsMessage>
    );
  };

  useEffect(() => {
    const fetchHeader = async () => {
      let headerResponse = await getHeaders();
      headerResponse = headerResponse?.data;
      if (headerResponse) {
        const normalizedOptions = [];
        const others = [];
        Object.keys(headerResponse).forEach((key) => {
          if (key !== "Date (MM/DD/YYYY)" && key !== "Date") {
            if (normalizedCategories.includes(key)) {
              normalizedOptions.push({
                label: key,
                value: headerResponse[key],
              });
            } else if (otherCategories.includes(key)) {
              others.push({ label: key, value: headerResponse[key] });
            }
          }
        });

        const options = [];
        options.push({
          label: t("ValueStrings.normalized"),
          options: normalizedOptions,
        });
        options.push({ label: t("ValueStrings.others"), options: others });
        setOptions(options);

        // Simplified logic for setting selected values
        const valuesToUse =
          !selectedValues || selectedValues.length === 0
            ? normalizedOptions
            : selectedValues;

        const updatedValues = createHeaderMappedValues(
          valuesToUse,
          headerResponse
        );
        setSelectedValues(updatedValues);
        dispatch(storeSelectedValues(updatedValues));

        setHeaderOptions(headerResponse);
        setInitialized(true);
      }
    };
    fetchHeader();
  }, []);

  const onCheckChange = (value) => {
    if (!headerOptions) return;

    setChartKey(Date.now().toString());

    const freshValues = createHeaderMappedValues(value, headerOptions);
    setSelectedValues(freshValues);
    dispatch(storeSelectedValues(freshValues));
  };

  const chartElements = React.useMemo(() => {
    if (!cleaningResponse || !selectedValues || !initialized) return null;

    const chartKeyPrefix = `chart-${chartKey}`;
    const plotKeyPrefix = `plot-${chartKey}`;

    return selectedValues.map((key, index) => (
      <div key={`${chartKeyPrefix}-${key.label}`}>
        <ChartPlot
          key={`${plotKeyPrefix}-${key.label}`}
          title={key.value}
          label={key.label}
          data={cleaningResponse[key.label] || []}
          color={getColor(key, index)}
          date={date}
          cips={cips}
          i18n={i18n}
          showCIPs
          threshold={getThreshold(key.label)}
        />
      </div>
    ));
  }, [selectedValues, cleaningResponse, chartKey, initialized, threshold]);

  return (
    <div>
      <div className="tabsContainer">
        {initialized && (
          <div>
            <div style={{ minWidth: "400px", marginRight: "20px" }}>
              <div className="select-popover-flex">
                <div className="select-container">
                  <Select
                    options={options}
                    name="label"
                    value={selectedValues}
                    isMulti
                    GroupType="cat"
                    onChange={onCheckChange}
                    placeholder={t("ValueStrings.select")}
                    components={{ NoOptionsMessage }}
                    getOptionLabel={(option) => option.value}
                  />
                </div>
                {activeStep === 1 ? (
                  <PopoverText
                    descriptor={<img src={InfoIconGreen} alt="info icon" />}
                    descriptionText={t(
                      "IdentifiedCIPStrings.plotVariablesDescription"
                    )}
                  />
                ) : activeStep === 2 ? (
                  <PopoverText
                    descriptor={<img src={InfoIconGreen} alt="info icon" />}
                    descriptionText={t(
                      "IdentifiedCIPStrings.plotVariablesDescription"
                    )}
                  />
                ) : null}
              </div>
            </div>

            {selectedValues && selectedValues.length > 0 && (
              <div
                style={{
                  display: "flex",
                  marginTop: "10px",
                  justifyContent: "center",
                }}
              >
                {(activeStep === 2 || activeStep === 3) && (
                  <div style={{ color: "red", fontSize: 12 }}>
                    <span
                      style={{
                        fontWeight: "bold",
                        fontSize: "16px",
                        marginRight: 2,
                      }}
                    >
                      {" "}
                      |
                    </span>
                    {t("IdentifiedCIPStrings.dottedLineCleaningAdvisor")}
                  </div>
                )}

                <div style={{ fontSize: 12, marginLeft: 16 }}>
                  <span
                    style={{
                      fontWeight: "bold",
                      fontSize: "16px",
                      marginRight: 2,
                    }}
                  >
                    {" "}
                    |
                  </span>
                  {t("IdentifiedCIPStrings.blackLines")}
                </div>
                <div style={{ color: "red", fontSize: 12, marginLeft: 16 }}>
                  <span
                    style={{ fontWeight: "bold", fontSize: 16, marginRight: 2 }}
                  >
                    {" "}
                    |
                  </span>{" "}
                  {t("IdentifiedCIPStrings.redLine")}
                </div>
              </div>
            )}
            <div className="popover-container">
              <PopoverText
                descriptor={<img src={InfoIconGreen} alt="info icon" />}
                descriptionText={
                  activeStep === 1
                    ? t("IdentifiedCIPStrings.plotDescriptionSection")
                    : activeStep === 2
                    ? t("IdentifiedCIPStrings.plotDescriptionSectionLine")
                    : ""
                }
              />
              <PopoverText
                descriptor={<img src={QuestionIcon} alt="question icon" />}
                descriptionText={
                  <span
                    dangerouslySetInnerHTML={{
                      __html: t(
                        "IdentifiedCIPStrings.troubleshootingCIPsAndCEBs"
                      ).replaceAll("<unit>", pressure || "bar/psig"),
                    }}
                  />
                }
              />
            </div>
            <div
              style={{
                marginTop: "20px",
                maxHeight: "70vh",
                overflowY: "scroll",
              }}
              className=""
            >
              <div key={`charts-wrapper-${chartKey}`}>{chartElements}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Charts;
